import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeftRight, BadgeCheck, Coins, ShieldX } from 'lucide-react'
import { formatDateTime } from '@/lib/datetime'
import { queryKeys } from '@/lib/query-client'
import type { AdminPaymentSummary } from '@/types/api'
import { Badge } from '@/components/ui/StatusBadge'
import { Button } from '@/components/ui/Button'
import { KeyValue, KeyValueGrid } from '@/components/ui/RowActions'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { PaymentProofUpload } from './PaymentProofUpload'
import { PaymentProofViewer } from './PaymentProofViewer'
import { SwitchToInstapayDialog } from './SwitchToInstapayDialog'
import { fetchBookingPointsHistory } from './api'
import {
  describePaymentMethod,
  describePointsCoverage,
  describePointsTransactionType,
  describeVerificationStatus,
  formatMoney,
  verificationTone,
} from './describe'
import styles from './PaymentDetailsPanel.module.css'

interface PaymentDetailsPanelProps {
  /** The booking's id. A trip must pass `trip.requestId` — a trip has no payment of its own. */
  tripRequestId: number | null
  payment: AdminPaymentSummary | null
  /** Shown in the switch-to-Instapay confirmation. Falls back to the id. */
  bookingReference?: string | null
  onVerify?: () => void
  onReject?: () => void
  /**
   * Called after a change this panel made itself — switching the method, or
   * attaching a receipt.
   *
   * Both hosts render a payment summary captured when the row was opened, so
   * after either change what is on screen is stale in exactly the way the
   * operator is about to act on. They use this to close, which lands the
   * operator back on a refreshed row — the same arrangement
   * {@link PaymentDetailsDialog} already uses after a verification decision.
   */
  onChanged?: () => void
}

/**
 * Everything about one booking's payment, for the details drawer on both trip
 * screens.
 *
 * Shows the whole split rather than a single total, because "EGP 500" answers
 * none of the questions an operator actually has: how much did the customer
 * transfer, how much came out of their points, and is anyone waiting on a
 * decision.
 */
export function PaymentDetailsPanel({
  tripRequestId,
  payment,
  bookingReference = null,
  onVerify,
  onReject,
  onChanged,
}: PaymentDetailsPanelProps) {
  // Owned here rather than raised to the two hosts. Both the trips drawer and the
  // requests dialog embed this panel, and threading the same dialog through both
  // would be two chances for them to offer the action on different rows.
  const [switching, setSwitching] = useState(false)

  // Only fetched when points were actually involved. The great majority of
  // bookings spend none, and a request per drawer open for an empty list is a
  // request for nothing.
  const pointsHistory = useQuery({
    queryKey: queryKeys.payments.points(tripRequestId ?? 0),
    queryFn: () => fetchBookingPointsHistory(tripRequestId!),
    enabled: tripRequestId !== null && (payment?.pointsUsedMinor ?? 0) > 0,
  })

  if (!payment || tripRequestId === null) {
    return <p className={styles.empty}>No payment record is linked to this booking.</p>
  }

  const coverage = describePointsCoverage(payment)
  const awaitingDecision = payment.canVerify && payment.verificationStatus === 'PENDING'

  return (
    <div className={styles.panel}>
      <KeyValueGrid>
        <KeyValue label="Payment method">{describePaymentMethod(payment)}</KeyValue>
        <KeyValue label="Payment status">
          <StatusBadge status={payment.paymentStatus} />
        </KeyValue>

        <KeyValue label="Total trip price">
          <strong className={styles.amount}>{formatMoney(payment.totalAmount, payment.currency)}</strong>
        </KeyValue>
        {payment.penaltyAmountMinor > 0 ? (
          <KeyValue label="Of which cancellation charge">
            {formatMoney(payment.penaltyAmount, payment.currency)}
          </KeyValue>
        ) : null}

        <KeyValue label="Paid with points">
          <span className={styles.amount}>{formatMoney(payment.pointsUsed, payment.currency)}</span>
        </KeyValue>
        <KeyValue label={`Paid by ${describePaymentMethod(payment).toLowerCase()}`}>
          <span className={styles.amount}>{formatMoney(payment.externalAmount, payment.currency)}</span>
        </KeyValue>

        {coverage ? (
          <KeyValue label="Points coverage">
            <Badge tone={payment.fullyCoveredByPoints ? 'success' : 'info'}>{coverage}</Badge>
          </KeyValue>
        ) : null}

        {payment.canVerify ? (
          <KeyValue label="Verification">
            <Badge tone={verificationTone(payment.verificationStatus)} dot>
              {describeVerificationStatus(payment.verificationStatus)}
            </Badge>
          </KeyValue>
        ) : null}
        {payment.verifiedAt ? (
          <KeyValue label="Reviewed by">
            {payment.verifiedByAdminName ?? `Admin #${payment.verifiedByAdminUserId ?? '—'}`} ·{' '}
            {formatDateTime(payment.verifiedAt)}
          </KeyValue>
        ) : null}
        {payment.verificationComment ? (
          <KeyValue label="Verification comment" wide>
            {payment.verificationComment}
          </KeyValue>
        ) : null}
      </KeyValueGrid>

      {/* Proof only exists where a human settles the payment. A card charge has
          no receipt to attach, so the whole section is absent rather than empty. */}
      {payment.canVerify ? (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Proof of payment</h3>
          <PaymentProofViewer
            tripRequestId={tripRequestId}
            hasProof={payment.hasProof}
            uploadedAt={payment.proofUploadedAt}
            sizeBytes={payment.proofSizeBytes}
          />
          {/*
            Rendered under the viewer rather than instead of it: replacing a
            receipt is a real case — the customer sent the wrong screenshot — and
            the operator should be able to see what is already on file before
            they overwrite it.
          */}
          {payment.canUploadProof ? (
            <PaymentProofUpload
              tripRequestId={tripRequestId}
              hasProof={payment.hasProof}
              onUploaded={onChanged}
            />
          ) : null}
        </section>
      ) : null}

      {/*
        The way out of a card booking whose payment never arrived. Offered only
        where the server says it would accept it — a live card booking, still
        pending, with no gateway transaction behind it — so the button and the
        endpoint cannot disagree.
      */}
      {payment.canConvertToInstapay ? (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Payment method</h3>
          <p className={styles.muted}>
            This card payment has not arrived. If the customer would rather transfer the money, switch
            the booking to Instapay and attach their receipt here.
          </p>
          <div className={styles.actions}>
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<ArrowLeftRight size={16} aria-hidden="true" />}
              onClick={() => setSwitching(true)}
            >
              Switch to Instapay
            </Button>
          </div>
        </section>
      ) : null}

      {awaitingDecision && (onVerify || onReject) ? (
        <div className={styles.actions}>
          {onVerify ? (
            <Button variant="primary" size="sm" iconLeft={<BadgeCheck size={16} />} onClick={onVerify}>
              Verify payment
            </Button>
          ) : null}
          {onReject ? (
            <Button variant="danger" size="sm" iconLeft={<ShieldX size={16} />} onClick={onReject}>
              Reject payment
            </Button>
          ) : null}
        </div>
      ) : null}

      {payment.pointsUsedMinor > 0 ? (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            <Coins size={14} aria-hidden="true" /> Points movements
          </h3>
          {pointsHistory.isPending ? (
            <p className={styles.muted}>Loading points history…</p>
          ) : pointsHistory.isError ? (
            <p className={styles.error}>The points history could not be loaded.</p>
          ) : pointsHistory.data && pointsHistory.data.length > 0 ? (
            <ul className={styles.ledger}>
              {pointsHistory.data.map((entry) => (
                <li key={entry.id} className={styles.ledgerRow}>
                  <span className={entry.credit ? styles.credit : styles.debit}>
                    {entry.credit ? '+' : ''}
                    {formatMoney(entry.amount, entry.currency)}
                  </span>
                  <span className={styles.ledgerType}>{describePointsTransactionType(entry.type)}</span>
                  <span className={styles.ledgerMeta}>
                    {entry.createdAt ? formatDateTime(entry.createdAt) : '—'}
                    {entry.createdByAdminName ? ` · ${entry.createdByAdminName}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles.muted}>No points movements are recorded for this booking.</p>
          )}
        </section>
      ) : null}

      <SwitchToInstapayDialog
        tripRequestId={switching ? tripRequestId : null}
        bookingReference={bookingReference}
        payment={payment}
        onClose={() => setSwitching(false)}
        onSwitched={onChanged}
      />
    </div>
  )
}
